import { create } from 'zustand'

import i18n from '../i18n'
import { languageAfterGeo } from '../lib/geoLocale'
import { PREFS_STORAGE_KEY as STORAGE_KEY } from '../lib/languagePref'
import { DEFAULT_SPEEDS, EVERY_DAY } from '../lib/settings'

const LANGUAGES = ['fa', 'en']

/**
 * The design's defaults: 1x, reminder off at 21:00 every day, Persian until
 * something says otherwise — 'auto' means no explicit choice has been made
 * yet, so App's geo bootstrap is still free to set `language` from the
 * caller's IP. Once a learner picks one in Settings it becomes 'manual' and
 * nothing but that learner ever changes it again.
 */
const DEFAULTS = {
  defaultSpeed: 1,
  reminder: { enabled: false, minuteOfDay: 21 * 60, days: EVERY_DAY },
  language: 'fa',
  languageSource: 'auto',
}

function load() {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null')
    if (!stored || typeof stored !== 'object') return DEFAULTS
    const speed = Number(stored.defaultSpeed)
    const reminder = stored.reminder ?? {}
    const days = Array.isArray(reminder.days) && reminder.days.length === 7 ? reminder.days.map(Boolean) : DEFAULTS.reminder.days
    const minuteOfDay = Number.isInteger(reminder.minuteOfDay) ? reminder.minuteOfDay : DEFAULTS.reminder.minuteOfDay
    return {
      defaultSpeed: DEFAULT_SPEEDS.includes(speed) ? speed : DEFAULTS.defaultSpeed,
      reminder: { enabled: Boolean(reminder.enabled), minuteOfDay, days },
      language: LANGUAGES.includes(stored.language) ? stored.language : DEFAULTS.language,
      languageSource: stored.languageSource === 'manual' ? 'manual' : DEFAULTS.languageSource,
    }
  } catch {
    // No storage (a private window, the unit environment): the defaults, and nothing remembered.
    return DEFAULTS
  }
}

function save(state) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        defaultSpeed: state.defaultSpeed,
        reminder: state.reminder,
        language: state.language,
        languageSource: state.languageSource,
      }),
    )
  } catch {
    // Storage refused: the setting still applies for this session.
  }
}

const initial = load()
void i18n.changeLanguage(initial.language)

/**
 * Device-local preferences (design page 18, `settings`): the default
 * playback speed the practice page starts from, the daily reminder, and the
 * interface language. All three live only on this device, like the Android
 * app's `AppPrefs` — the server has no opinion about any of them, `/geo/locale`
 * only ever supplies a starting guess for `language`.
 */
export const usePrefsStore = create((set, get) => ({
  ...initial,

  // Set from `/geo/locale`, never stored: while it is true the interface is
  // the one the caller's country dictates and the FA/EN toggles are gone.
  // It is recomputed on every load, so a visitor who travels is not stuck
  // with yesterday's answer.
  languageLocked: false,

  setDefaultSpeed(speed) {
    if (!DEFAULT_SPEEDS.includes(speed)) return
    set({ defaultSpeed: speed })
    save(get())
  },

  setReminder(reminder) {
    set({ reminder: { ...get().reminder, ...reminder } })
    save(get())
  },

  /**
   * `source` is 'manual' for an explicit choice in Settings (locks out any
   * future auto-detection) and 'auto' for the geo bootstrap's guess — which
   * only ever calls this while `languageSource` is still 'auto', so it can
   * never clobber a learner's own pick.
   */
  setLanguage(language, source = 'manual') {
    if (!LANGUAGES.includes(language)) return
    set({ language, languageSource: source })
    save(get())
    void i18n.changeLanguage(language)
  },

  /**
   * The answer from `/geo/locale`, applied under the two rules of
   * `lib/geoLocale.js`: abroad it is a starting guess this drops the moment
   * a learner has chosen; from Iran it is the law, and the Persian site is
   * the only one rendered.
   *
   * The lock renders *over* the stored preference rather than overwriting
   * it — nothing is saved — so a learner who once picked English here is
   * given English back the first time they open the app from abroad.
   */
  applyGeo(geo) {
    const { language, locked } = languageAfterGeo(geo, get())
    set({ languageLocked: locked })
    if (locked) {
      void i18n.changeLanguage(language)
      return
    }
    // Only when it actually differs: calling setLanguage otherwise would
    // downgrade a learner's 'manual' back to 'auto' for no reason.
    if (language !== get().language) get().setLanguage(language, 'auto')
  },
}))
